import { useState } from 'react';
import {
  Typography,
  Divider,
  TextField,
  Stack,
  Autocomplete,
  Chip,
  Button,
} from '@mui/material';
import { Box } from '@mui/system';

const TodoConfigSetting = ({ deployment, onSave }) => {
  const [config, setConfig] = useState({
    backofficeDepartmentId: deployment?.config?.backofficeDepartmentId || '',
    salesDepartmentId: deployment?.config?.salesDepartmentId || '',
    salesTags: deployment?.config?.salesTags || [],
    importantTags: deployment?.config?.importantTags || [],
  });

  const [errors, setErrors] = useState({
    backofficeDepartmentId: !deployment?.config?.backofficeDepartmentId
      ? 'Dieses Feld ist erforderlich'
      : '',
    salesDepartmentId: !deployment?.config?.salesDepartmentId
      ? 'Dieses Feld ist erforderlich'
      : '',
  });

  const handleNumberChange = (field) => (event) => {
    const value = event.target.value;
    const numValue = parseInt(value);

    if (numValue && numValue < 0) return;

    setConfig((prev) => ({ ...prev, [field]: numValue }));

    if (!value) {
      setErrors((prev) => ({
        ...prev,
        [field]: 'Dieses Feld ist erforderlich',
      }));
    } else if (!Number.isInteger(numValue) || numValue < 0) {
      setErrors((prev) => ({
        ...prev,
        [field]: 'Bitte geben Sie eine gültige positive Zahl ein',
      }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleTagsChange = (field) => (_, newValue) => {
    const validTags = newValue
      .filter((value) => /^\d+$/.test(value))
      .map((value) => ({ id: parseInt(value) }));

    setConfig((prev) => ({
      ...prev,
      [field]: validTags,
    }));
  };

  const handleTagInput = (event) => {
    // Only allow numeric input
    if (!/^\d*$/.test(event.target.value)) {
      event.preventDefault();
    }
  };

  const handleSave = () => {
    // Filter out empty values
    const configuredValues = Object.entries(config).reduce(
      (acc, [key, value]) => {
        if (value) acc[key] = value;
        return acc;
      },
      {},
    );

    onSave(configuredValues);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ my: 1, mb: 2, pr: '80px' }}>
        <Typography variant="h6">Vorlagen für Belege</Typography>
        <Divider />
      </Box>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Konfigurieren Sie die Abteilungs-IDs und Tags für Ihr Todo-Tool.
        Abteilungs-IDs sind erforderlich.
      </Typography>
      <Stack spacing={3}>
        <TextField
          required
          fullWidth
          label="Backoffice Abteilungs-ID"
          value={config.backofficeDepartmentId}
          onChange={handleNumberChange('backofficeDepartmentId')}
          error={!!errors.backofficeDepartmentId}
          helperText={errors.backofficeDepartmentId || ''}
          type="number"
        />

        <TextField
          required
          fullWidth
          label="Vertrieb Abteilungs-ID"
          value={config.salesDepartmentId}
          onChange={handleNumberChange('salesDepartmentId')}
          error={!!errors.salesDepartmentId}
          helperText={errors.salesDepartmentId || ''}
          type="number"
        />

        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={config.salesTags.map((tag) => tag.id.toString())}
          onChange={handleTagsChange('salesTags')}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={index}
                label={`Tag ID: ${option}`}
                {...getTagProps({ index })}
                color="primary"
                variant="outlined"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vertriebs-Tags"
              placeholder="Tag-ID eingeben (nur Zahlen)"
              onKeyPress={handleTagInput}
              helperText="Geben Sie eine Tag-ID ein und drücken Sie Enter"
            />
          )}
        />

        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={config.importantTags.map((tag) => tag.id.toString())}
          onChange={handleTagsChange('importantTags')}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={index}
                label={`Tag ID: ${option}`}
                {...getTagProps({ index })}
                color="primary"
                variant="outlined"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Wichtige Tags"
              placeholder="Tag-ID eingeben (nur Zahlen)"
              onKeyPress={handleTagInput}
              helperText="Geben Sie eine Tag-ID ein und drücken Sie Enter"
            />
          )}
        />
      </Stack>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSave}
        sx={{ mt: 'auto', ml: 'auto' }} // This pushes the button to the bottom right
      >
        Speichern
      </Button>
    </Box>
  );
};

export default TodoConfigSetting;
